import { useEffect, useMemo, useState } from "react";
import type { RiskLimit } from "../types";
import { coolingOffFromNow } from "../lib/coolingOff";

// Loosen-config modal — UX §13.2.2 + PRD §5.2.
//   • New value must be strictly greater than the current value (loosening).
//   • Reflection ≥ 200 characters, required.
//   • Acknowledgment: the user confirms the configured activation delay.
//   • For daily/weekly loss limits, max-once-per-quarter cap applies (PRD §5.2);
//     when used, Submit is disabled and the warning banner names the cap.
//   • On submit, the parent appends a PendingConfigChange row with
//     activatesAtMs = now() + the configured duration and surfaces it
//     on the Dashboard until activation or cancellation.

const MIN_REFLECTION_CHARS = 200;

type Props = {
  limit: RiskLimit;
  coolingOffDays: number;
  onClose: () => void;
  onSubmit: (newValue: number, reflection: string) => void;
};

const daysLabel = (days: number): string =>
  `${days}-day`;

export const LoosenConfigModal = ({
  limit,
  coolingOffDays,
  onClose,
  onSubmit,
}: Props) => {
  const stamp = useMemo(
    () => coolingOffFromNow(coolingOffDays),
    [coolingOffDays],
  );
  const [newValue, setNewValue] = useState<string>(String(limit.value));
  const [reflection, setReflection] = useState("");
  const [ack, setAck] = useState(false);

  const newValueNum = Number(newValue);
  const newValueValid =
    Number.isFinite(newValueNum) && newValueNum > limit.value;

  const reflectionLen = reflection.length;
  const reflectionOk = reflectionLen >= MIN_REFLECTION_CHARS;

  const capExhausted =
    limit.quarterlyCap &&
    (limit.loosensThisQuarter ?? 0) >= (limit.loosensCapPerQuarter ?? 0);

  const ready = newValueValid && reflectionOk && ack && !capExhausted;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="tp-modal-back" onClick={onClose} role="presentation">
      <div
        className="tp-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="loosen-modal-title"
      >
        <div className="tp-modal-head">
          <div>
            <div className="title" id="loosen-modal-title">
              Loosen {limit.label}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--tp-fg-3)",
                marginTop: 2,
                fontFamily: "var(--tp-font-mono)",
              }}
            >
              {daysLabel(coolingOffDays)} cooling-off · activates {stamp.display}
            </div>
          </div>
        </div>

        <div className="tp-modal-body">
          <div
            className="tp-loosen-values"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 4,
                }}
              >
                Current
              </div>
              <div
                className="num"
                style={{ fontSize: 22, fontWeight: 600 }}
              >
                {limit.value}
                {limit.unit === "%" ? "%" : ""}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 4,
                }}
              >
                New
              </div>
              <input
                type="number"
                step={limit.unit === "%" ? "0.1" : "1"}
                className="tp-input num"
                style={{ fontSize: 18, fontWeight: 600, width: "100%" }}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                aria-label={`New value for ${limit.label}`}
              />
              {!newValueValid && newValue !== "" && (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--tp-bad)",
                    marginTop: 4,
                  }}
                >
                  New value must be greater than {limit.value}
                  {limit.unit === "%" ? "%" : ""} (loosening only).
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              padding: "10px 12px",
              background: "var(--tp-info-soft)",
              border: "1px solid var(--tp-info-line)",
              borderLeft: "3px solid var(--tp-info)",
              borderRadius: 4,
              fontSize: 13,
              lineHeight: 1.45,
              color: "var(--tp-fg-1)",
              marginBottom: 14,
            }}
          >
            Loosening goes through a {daysLabel(coolingOffDays)} cooling-off
            period. The change will activate on{" "}
            <span className="num" style={{ fontWeight: 500 }}>
              {stamp.display}
            </span>{" "}
            unless canceled.
          </div>

          <label
            htmlFor="reflection"
            style={{
              display: "block",
              fontSize: 11,
              color: "var(--tp-fg-3)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom: 6,
            }}
          >
            Reflection (required, ≥ 200 characters)
          </label>
          <textarea
            id="reflection"
            className="tp-input"
            placeholder="Why are you loosening this limit? What evidence supports it?"
            rows={6}
            style={{ width: "100%", resize: "vertical" }}
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            aria-describedby="reflection-counter"
          />
          <div
            id="reflection-counter"
            style={{
              textAlign: "right",
              fontSize: 11,
              color: reflectionOk ? "var(--tp-fg-3)" : "var(--tp-warn)",
              marginTop: 4,
              fontFamily: "var(--tp-font-mono)",
            }}
          >
            ({reflectionLen} / {MIN_REFLECTION_CHARS})
          </div>

          <div className="tp-ack" style={{ marginTop: 12 }}>
            <input
              id="ack-cooling-off"
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <label htmlFor="ack-cooling-off">
              I understand this change activates in {coolingOffDays} day
              {coolingOffDays === 1 ? "" : "s"} unless I cancel.
            </label>
          </div>

          {limit.quarterlyCap && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 12px",
                background: capExhausted
                  ? "var(--tp-bad-soft)"
                  : "var(--tp-warn-soft)",
                border:
                  "1px solid " +
                  (capExhausted ? "var(--tp-bad-line)" : "var(--tp-warn-line)"),
                borderLeft:
                  "3px solid " +
                  (capExhausted ? "var(--tp-bad)" : "var(--tp-warn)"),
                borderRadius: 4,
                fontSize: 12,
                lineHeight: 1.45,
                color: "var(--tp-fg-1)",
              }}
              role="status"
            >
              ⚠ You have used {limit.loosensThisQuarter ?? 0} of your{" "}
              {limit.loosensCapPerQuarter ?? 0} allowed{" "}
              {limit.key === "daily_loss" ? "daily-loss" : "weekly-loss"}{" "}
              loosens per quarter (PRD §5.1).
              {capExhausted && (
                <>
                  {" "}
                  <b>Submit is blocked until next calendar quarter.</b>
                </>
              )}
            </div>
          )}
        </div>

        <div className="tp-modal-foot">
          <span
            className="num"
            style={{ color: "var(--tp-fg-3)", fontSize: 11 }}
          >
            <span className="tp-kbd">Esc</span> cancels
          </span>
          <div className="actions">
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              disabled={!ready}
              onClick={() => onSubmit(newValueNum, reflection)}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
